import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import {
  IOptimizeImageOptions,
  optimizeImageUrl,
} from "@faire/web--source/common/images/optimizeImages";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web--source/common/media";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import {
  ResponsiveProp,
  isResponsiveProp,
} from "@faire/web--source/slate/Theme/ResponsiveProp";
import { ResponsiveValues } from "@faire/web--source/slate/Theme/ResponsiveValues";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React from "react";

export const LoggedOutHomeImage: React.FC<{
  responsiveImage: ResponsiveValues<IImage>;
  optimizeImageOptions: ResponsiveProp<IOptimizeImageOptions>;
  className?: string;
  altText?: string;
  lazyLoad?: boolean;
  imageStyles?: React.CSSProperties;
  imageWrapperStyles?: React.CSSProperties;
  // eslint-disable-next-line complexity
}> = ({
  responsiveImage,
  optimizeImageOptions,
  className,
  altText,
  lazyLoad,
  imageStyles,
  imageWrapperStyles,
}) => {
  const imageOptionsMobile = isResponsiveProp(optimizeImageOptions)
    ? optimizeImageOptions.mobileAndAbove
    : optimizeImageOptions;
  const imageOptionsTablet = isResponsiveProp(optimizeImageOptions)
    ? optimizeImageOptions.tabletAndAbove
    : optimizeImageOptions;
  const imageOptionsDesktop = isResponsiveProp(optimizeImageOptions)
    ? optimizeImageOptions.desktopAndAbove
    : optimizeImageOptions;
  const imageOptionsXLarge = isResponsiveProp(optimizeImageOptions)
    ? optimizeImageOptions.xLargeAndAbove
    : optimizeImageOptions;
  const imageOptionsXXLarge = isResponsiveProp(optimizeImageOptions)
    ? optimizeImageOptions.xxLargeDesktop
    : optimizeImageOptions;

  const isSSR = useIsSSR();

  const imageMobile = responsiveImage.mobileAndAbove;
  const imageTablet = responsiveImage.tabletAndAbove;
  const imageDesktop = responsiveImage.desktopAndAbove;
  const imageXLarge = responsiveImage.xLargeAndAbove;
  const imageXXLarge = responsiveImage.xxLargeDesktop;
  if (
    !imageMobile &&
    !imageTablet &&
    !imageDesktop &&
    !imageXLarge &&
    !imageXXLarge
  ) {
    return null;
  }

  const imageUrlMobile = getOptimizedImageUrl(
    imageMobile,
    imageOptionsMobile,
    isSSR
  );

  const imageUrlTablet =
    imageOptionsTablet || imageTablet
      ? getOptimizedImageUrl(
          imageTablet ?? imageMobile,
          imageOptionsTablet ?? imageOptionsMobile,
          isSSR
        )
      : undefined;

  const imageUrlDesktop =
    imageOptionsDesktop || imageDesktop
      ? getOptimizedImageUrl(
          imageDesktop ?? imageTablet ?? imageMobile,
          imageOptionsDesktop ?? imageOptionsTablet ?? imageOptionsMobile,
          isSSR
        )
      : undefined;

  const imageUrlXLarge =
    imageOptionsXLarge || imageXLarge
      ? getOptimizedImageUrl(
          imageXLarge ?? imageDesktop ?? imageTablet ?? imageMobile,
          imageOptionsXLarge ??
            imageOptionsDesktop ??
            imageOptionsTablet ??
            imageOptionsMobile,
          isSSR
        )
      : undefined;

  const imageUrlXXLarge =
    imageOptionsXXLarge || imageXXLarge
      ? getOptimizedImageUrl(
          imageXXLarge ??
            imageXLarge ??
            imageDesktop ??
            imageTablet ??
            imageMobile,
          imageOptionsXXLarge ??
            imageOptionsXLarge ??
            imageOptionsDesktop ??
            imageOptionsTablet ??
            imageOptionsMobile,
          isSSR
        )
      : undefined;

  return (
    <picture className={className} style={imageWrapperStyles}>
      {imageUrlXXLarge ? (
        <source media="(min-width: 1728px)" srcSet={imageUrlXXLarge} />
      ) : null}
      {imageUrlXLarge ? (
        <source
          media={`(min-width: ${MIN_WIDTH_BREAKPOINTS.XLARGE}px)`}
          srcSet={imageUrlXLarge}
        />
      ) : null}
      {imageUrlDesktop ? (
        <source
          media={`(min-width: ${MIN_WIDTH_BREAKPOINTS.DESKTOP}px)`}
          srcSet={imageUrlDesktop}
        />
      ) : null}
      {imageUrlTablet ? (
        <source
          media={`(min-width: ${MIN_WIDTH_BREAKPOINTS.TABLET}px)`}
          srcSet={imageUrlTablet}
        />
      ) : null}
      <img
        style={{
          objectFit: "cover",
          width: "100%",
          height: "100%",
          ...imageStyles,
        }}
        src={imageUrlMobile ?? EAEAEA_PIXEL}
        alt={altText ?? imageMobile.alt_text}
        loading={lazyLoad ? "lazy" : undefined}
        width="100%"
        height="100%"
      />
    </picture>
  );
};

const getOptimizedImageUrl = (
  image: IImage,
  optimizeImageOptions: IOptimizeImageOptions,
  isSSR: boolean
) => {
  return optimizeImageUrl({ url: image.url }, isSSR, optimizeImageOptions);
};
